/**
 * Dashboard stat card keys — mirrors `/api/constants/dashboard-stats` allowed values.
 * @typedef {Object} IDashboardStatOption
 * @property {string} key
 * @property {string} label
 * @property {string} [description]
 */

/** @type {IDashboardStatOption[]} */
export const DASHBOARD_STAT_OPTIONS = [
  { key: "total_cts", label: "Total Studies", description: "All studies in scope" },
  // { key: "today_cts", label: "Today", description: "Studies uploaded today" },
  // { key: "week_cts", label: "This Week", description: "Studies uploaded this week" },
  // { key: "month_cts", label: "This Month", description: "Studies uploaded this month" },
  { key: "patients", label: "Patients", description: "Total patients" },
  { key: "organizations", label: "Hospitals", description: "Organizations / hospitals" },
  { key: "doctors", label: "Doctors", description: "Hospital doctors" },
  // { key: "status_uploaded", label: "Uploaded", description: "Studies with status uploaded" },
  // { key: "status_in_review", label: "In Review", description: "Studies in review" },
  // { key: "status_report_ready", label: "Report Ready", description: "Studies with report ready" },
  { key: "pending_patients", label: "Pending Patients", description: "Patients awaiting action" },
  { key: "ct_pending_report", label: "CT Ratio", description: "CT images vs CT reports vs awaiting report" },
  { key: "anonymized_patients", label: "Anonymized Patients", description: "Patients with PHI cleared" },
  { key: "unanonymized_patients", label: "Unanonymized Patients", description: "Patients with PHI visible" },
  { key: "valve_manufacturer_summary", label: "Valve Manufacturers", description: "Medtronic vs competition counts" },
  { key: "patient_anatomy_summary", label: "Implants", description: "Counts by valve manufacturer" },
  { key: "modality_distribution", label: "Studies by Modality", description: "Breakdown by modality" },
];

/** @type {{ key: string, title: string }[]} */
export const PATIENT_ANATOMY_SUMMARY_ITEMS = [
  { key: "medtronic", title: "Medtronic" },
  { key: "abbott", title: "Abbott" },
  { key: "edwards", title: "Edwards" },
  { key: "meril", title: "Meril" },
  { key: "smt", title: "SMT" },
  { key: "microport", title: "Microport" },
  { key: "allegra", title: "Allegra" },
  { key: "boston_scientific", title: "Boston Scientific" },
  { key: "corcym", title: "Corcym" },
  { key: "others", title: "Others" },
];

export const DASHBOARD_STAT_KEYS = DASHBOARD_STAT_OPTIONS.map((o) => o.key);

const ADMIN_ROLES = new Set([
  "super_admin",
  "admin",
  "owner",
  "hospital_admin",
  "company_admin",
]);

/** Whether this role uses the admin dashboard-stats config. */
export const isDashboardAdminRole = (role) => {
  if (!role) return false;
  return ADMIN_ROLES.has(String(role).trim().toLowerCase());
};

/**
 * Normalize various API response shapes into a string[].
 * @param {unknown} data
 * @param {"admin" | "users"} [audience]
 * @returns {string[]}
 */
export const extractAllowedStats = (data, audience) => {
  if (!data) return [];

  if (Array.isArray(data)) {
    return data.filter((k) => typeof k === "string");
  }

  if (typeof data !== "object") return [];

  const obj = /** @type {Record<string, unknown>} */ (data);

  const fromAllowed = obj.allowed_dashboard_stats;
  if (Array.isArray(fromAllowed)) {
    return fromAllowed.filter((k) => typeof k === "string");
  }

  if (audience === "admin") {
    const admin = obj.admin ?? obj.admin_allowed_dashboard_stats ?? obj.admin_stats;
    if (Array.isArray(admin)) return admin.filter((k) => typeof k === "string");
    if (admin && typeof admin === "object") {
      const nested = /** @type {Record<string, unknown>} */ (admin).allowed_dashboard_stats;
      if (Array.isArray(nested)) return nested.filter((k) => typeof k === "string");
    }
  }

  if (audience === "users") {
    const users = obj.users ?? obj.user_allowed_dashboard_stats ?? obj.users_stats ?? obj.user;
    if (Array.isArray(users)) return users.filter((k) => typeof k === "string");
    if (users && typeof users === "object") {
      const nested = /** @type {Record<string, unknown>} */ (users).allowed_dashboard_stats;
      if (Array.isArray(nested)) return nested.filter((k) => typeof k === "string");
    }
  }

  return [];
};

/**
 * Parse GET /constants/dashboard-stats/all into admin + users arrays.
 * @param {unknown} data
 * @returns {{ admin: string[], users: string[] }}
 */
export const parseDashboardStatsAll = (data) => ({
  admin: extractAllowedStats(data, "admin"),
  users: extractAllowedStats(data, "users"),
});

/** @param {string[] | null | undefined} allowed — null = show all (config missing) */
export const isStatAllowed = (allowed, key) => {
  if (allowed == null) return true;
  if (!Array.isArray(allowed)) return true;
  return allowed.includes(key);
};
